import {
	EducationInterface,
	ExperienceInterface,
	LanguageInterface,
	PreferencesInterface,
	CertificationsInterface,
	ResumeInterface,
	VideoResumeInterface,
	ProjectInterface,
	PersonalInfoInterface,
	ChangePasswordInterface,
	DeleteModalInterface,
} from 'constants/interfaces';
import * as Yup from 'yup';

type TranslateFunction = (key: string, params?: Record<string, string>) => string;

export const preferencesInitialValues: PreferencesInterface = {
	countryIds: [],
	stateIds: [],
	cityIds: [],
	language: [],
	newRole: [],
	newSkill: [],
	jobRole: [],
	level: [],
	isCompanySizeSpecific: '',
	companySize: '',
	isIndustoriesFavourite: '',
	industries: [],
	salary: 1,
	locations: [],
	skills: [],
};

export const languageInitialValues: LanguageInterface = {
	title: '',
	proficiency: '',
};

export const experienceInitialValues: ExperienceInterface = {
	jobTitle: '',
	employer: '',
	employerId: null,
	startDate: '',
	endDate: '',
	rolesAndResponsibility: '',
	currentlyWorking: false,
};

export const projectInitialValues: ProjectInterface = {
	title: '',
	fromDate: '',
	toDate: '',
	noOfParticipation: null,
	description: '',
};

export const educationInitialValues: EducationInterface = {
	universityName: '',
	degree: '',
	graduationDate: '',
	graduationEndDate: '',
	currentlyStudying: false,
};

export const certificationInitialValues: CertificationsInterface = {
	instituteName: '',
	courseName: '',
	completionYear: null,
};

export const resumeInitialValues: ResumeInterface = {
	title: '',
	path: '',
};

export const videoResumeInitialValues: VideoResumeInterface = {
	videoResume: '',
};

export const personalInfoInitialValues: PersonalInfoInterface = {
	firstName: '',
	lastName: '',
	designation: '',
	countryId: null,
	stateId: null,
	cityId: null,
	address: '',
	phoneNumber: '',
	countryCode: '91',
	totalExperience: 0,
};

export const changePasswordInitialValues: ChangePasswordInterface = {
	currentPassword: '',
	newPassword: '',
	confirmPassword: '',
};
export const deleteModalInitialValues: DeleteModalInterface = {
	reason: '',
	description: '',
};

export const resumeSchema = (translate: TranslateFunction) =>
	Yup.object().shape({
		title: Yup.string()
			.required(translate('Validation.required', { field: translate('PlaceHolder.title') }))
			.trim(),
		path: Yup.string()
			.required(translate('Validation.required', { field: translate('Modal.resume') }))
			.trim(),
	});

export const personalInfoSchema = (translate: TranslateFunction) =>
	Yup.object().shape({
		firstName: Yup.string()
			.required(translate('Validation.required', { field: translate('Common.firstNameCommon') }))
			.trim(),
		lastName: Yup.string()
			.required(translate('Validation.required', { field: translate('Common.lastNameCommon') }))
			.trim(),
		designation: Yup.string()
			.required(translate('Validation.required', { field: translate('Common.designation') }))
			.trim(),
		countryId: Yup.string().required(
			translate('Validation.required', { field: translate('Validation.countryId') }),
		),
		stateId: Yup.string().required(
			translate('Validation.required', { field: translate('Validation.stateIdId') }),
		),
		cityId: Yup.string().required(
			translate('Validation.required', { field: translate('Validation.cityId') }),
		),
		address: Yup.string().required(
			translate('Validation.required', { field: translate('Resume.address') }),
		),
		phoneNumber: Yup.string()
			.required(translate('Validation.required', { field: translate('PlaceHolder.phone') }))
			.min(5, translate('Validation.phoneNumberMustBeAtleast'))
			.max(
				12,
				translate('Validation.max', { field: translate('PlaceHolder.phone'), number: '12' }),
			),
		countryCode: Yup.string().required(
			translate('Validation.required', { field: translate('Validation.countryCode') }),
		),
		totalExperience: Yup.number()
			.required(translate('Validation.required', { field: translate('PlaceHolder.experience') }))
			.min(
				0,
				translate('Validation.max', { field: translate('PlaceHolder.experience'), number: '0' }),
			)
			.max(
				50,
				translate('Validation.max', { field: translate('PlaceHolder.experience'), number: '50' }),
			),
	});

export const videoResumeSchema = (translate: TranslateFunction) =>
	Yup.object().shape({
		videoResume: Yup.string().required(
			translate('Validation.required', { field: translate('Modal.resume') }),
		),
	});
export const experienceSchema = (translate: TranslateFunction) =>
	Yup.object().shape({
		jobTitle: Yup.string()
			.required(translate('Validation.required', { field: translate('Job.jobTile') }))
			.trim(),
		employer: Yup.string()
			.required(translate('Validation.required', { field: translate('Job.employer') }))
			.trim(),
		startDate: Yup.date().required(
			translate('Validation.required', { field: translate('PlaceHolder.startDate') }),
		),
		endDate: Yup.date().when('currentlyWorking', {
			is: false,
			then: (schema) =>
				schema
					.required(translate('Validation.required', { field: translate('PlaceHolder.endDate') }))
					.min(
						Yup.ref('startDate'),
						translate('Validation.endDateAfterStartDate', {
							field: translate('PlaceHolder.endDate'),
						}),
					),
			otherwise: (schema) => schema.optional(),
		}),
		rolesAndResponsibility: Yup.string()
			.required(
				translate('Validation.required', {
					field: translate('PlaceHolder.rolesAndResponsibilities'),
				}),
			)
			.max(
				500,
				translate('Validation.max', {
					field: translate('PlaceHolder.rolesAndResponsibilities'),
					number: '500',
				}),
			)
			.trim(),
	});

export const projectSchema = (translate: TranslateFunction) =>
	Yup.object().shape({
		title: Yup.string()
			.required(translate('Validation.required', { field: translate('PlaceHolder.title') }))
			.trim(),
		noOfParticipation: Yup.number()
			.required(
				translate('Validation.required', { field: translate('Validation.noOfParticipation') }),
			)
			.min(1, 'Minimum number of participation is 1'),
		fromDate: Yup.date().required(
			translate('Validation.required', { field: translate('Validation.fromDate') }),
		),
		toDate: Yup.date()
			.required(translate('Validation.required', { field: translate('Validation.toDate') }))
			.min(
				Yup.ref('fromDate'),
				translate('Validation.endDateAfterStartDate', { field: translate('Validation.toDate') }),
			),
		description: Yup.string()
			.required(translate('Validation.required', { field: translate('Validation.description') }))
			.max(
				500,
				translate('Validation.max', { field: translate('Validation.description'), number: '500' }),
			)
			.trim(),
	});

export const certificationSchema = (translate: TranslateFunction) =>
	Yup.object().shape({
		instituteName: Yup.string()
			.required(translate('Validation.required', { field: translate('PlaceHolder.instituteName') }))
			.trim(),
		courseName: Yup.string()
			.required(translate('Validation.required', { field: translate('Profile.courseName') }))
			.trim(),
		completionYear: Yup.string().required(
			translate('Validation.required', { field: translate('Profile.completionYear') }),
		),
	});

export const educationSchema = (translate: TranslateFunction) =>
	Yup.object().shape({
		universityName: Yup.string()
			.required(translate('Validation.required', { field: translate('Profile.universityName') }))
			.trim(),
		degree: Yup.string()
			.required(translate('Validation.required', { field: translate('PlaceHolder.degree') }))
			.trim(),
		graduationDate: Yup.date().required(
			translate('Validation.required', { field: translate('PlaceHolder.startDate') }),
		),
		graduationEndDate: Yup.date().when('currentlyStudying', {
			is: false,
			then: (schema) =>
				schema
					.required(translate('Validation.required', { field: translate('PlaceHolder.endDate') }))
					.min(
						Yup.ref('graduationDate'),
						translate('Validation.endDateAfterStartDate', {
							field: translate('PlaceHolder.endDate'),
						}),
					),

			otherwise: (schema) => schema.optional(),
		}),
	});

export const languageSchema = (translate: TranslateFunction) =>
	Yup.object().shape({
		title: Yup.string()
			.required(translate('Validation.required', { field: translate('PlaceHolder.title') }))
			.trim(),
		proficiency: Yup.string().required(
			translate('Validation.required', { field: translate('PlaceHolder.proficiency') }),
		),
	});
export const skillSchema = (translate: TranslateFunction) =>
	Yup.object().shape({
		skills: Yup.array()
			.required(translate('Validation.required', { field: translate('PlaceHolder.title') }))
			.min(
				1,
				translate('Validation.minArr', { field: translate('PlaceHolder.skills'), number: '1' }),
			)
			.max(
				15,
				translate('Validation.maxArr', { field: translate('PlaceHolder.skills'), number: '15' }),
			),
	});
export const preferencesSchema = (translate: TranslateFunction) =>
	Yup.object()
		.shape({
			companySize: Yup.string().when('isCompanySizeSpecific', {
				is: (val: string) => val === 'yes',
				then: (schema) =>
					schema
						.required(translate('Validation.required', { field: translate('Company.CompanySize') }))
						.min(
							1,
							translate('Validation.minArr', {
								field: translate('Company.CompanySize'),
								number: '1',
							}),
						),
				otherwise: (schema) => schema.optional(),
			}),
			newRole: Yup.array(),
			level: Yup.array()
				.required(translate('Validation.required', { field: translate('Common.level') }))
				.min(1, translate('Validation.minArr', { field: translate('Common.level'), number: '1' })),
			isCompanySizeSpecific: Yup.string().required(
				translate('Validation.required', { field: translate('Validation.companySizeSpecific') }),
			),
			jobRole: Yup.array()
				.required(translate('Validation.required', { field: translate('Validation.role') }))
				.min(
					1,
					translate('Validation.minArr', { field: translate('Validation.role'), number: '1' }),
				),
			language: Yup.array()
				.required(translate('Validation.required', { field: translate('PlaceHolder.language') }))
				.min(
					1,
					translate('Validation.minArr', { field: translate('PlaceHolder.language'), number: '1' }),
				)
				.max(
					7,
					translate('Validation.maxArr', { field: translate('PlaceHolder.language'), number: '7' }),
				),
			isIndustoriesFavourite: Yup.string().required(
				translate('Validation.required', { field: translate('Validation.isIndustriesFavorite') }),
			),
			industries: Yup.array().when('isIndustoriesFavourite', {
				is: (val: string) => val === 'yes',
				then: (schema) =>
					schema
						.required(
							translate('Validation.required', { field: translate('PlaceHolder.industry') }),
						)
						.min(
							1,
							translate('Validation.minArr', {
								field: translate('PlaceHolder.industry'),
								number: '1',
							}),
						)
						.max(
							5,
							translate('Validation.maxArr', {
								field: translate('PlaceHolder.industry'),
								number: '6',
							}),
						),
				otherwise: (schema) => schema.optional(),
			}),
			salary: Yup.number().required(
				translate('Validation.required', { field: translate('JobDetailBox.salary') }),
			),
			locations: Yup.array()
				.required(translate('Validation.required', { field: translate('PlaceHolder.location') }))
				.min(
					1,
					translate('Validation.minArr', { field: translate('PlaceHolder.location'), number: '1' }),
				)
				.max(
					24,
					translate('Validation.maxArr', {
						field: translate('PlaceHolder.location'),
						number: '25',
					}),
				),
			skills: Yup.array()
				.required(translate('Validation.required', { field: translate('Common.skill') }))
				.min(1, translate('Validation.minArr', { field: translate('Common.skill'), number: '1' }))
				.max(
					15,
					translate('Validation.maxArr', { field: translate('Common.skill'), number: '15' }),
				),
		})
		.test('maxRoles', function (value) {
			const { newRole, jobRole } = value;
			const totalRoles = (newRole?.length || 0) + (jobRole?.length || 0);
			if (totalRoles > 10) {
				return this.createError({
					path: 'jobRole',
					message: translate('Validation.maxArr', {
						field: translate('JobDetailBox.roles'),
						number: '11',
					}),
				});
			}
			return true;
		});

export const changePasswordSchema = (translate: TranslateFunction) =>
	Yup.object().shape({
		currentPassword: Yup.string().required(
			translate('Validation.required', { field: translate('Validation.currentPassword') }),
		),
		newPassword: Yup.string()
			.required(
				translate('Validation.required', { field: translate('Validation.currentPassword') }),
			)
			.min(
				6,
				translate('Validation.minArr', {
					field: translate('Validation.currentPassword'),
					number: '8',
				}),
			)
			.test(
				'not-same-as-current',
				translate('Validation.newPasswordMustBeDifferent'), // Example translation key
				function (value) {
					return value !== this.parent.currentPassword;
				},
			),
		confirmPassword: Yup.string()
			.oneOf([Yup.ref('newPassword')], translate('Validation.passwordsMustMatch'))
			.required(translate('Validation.confirmPasswordIsRequired')),
	});

export const deleteModalSchema = (translate: TranslateFunction) =>
	Yup.object().shape({
		reason: Yup.string()
			.required(translate('Validation.required', { field: translate('Modal.reason') }))
			.trim(),
		description: Yup.string()
			.required(translate('Validation.required', { field: translate('Modal.description') }))
			.trim(),
	});
